import React, { useMemo, useCallback, useState } from 'react'
import { useQuery, useMutation } from '@apollo/client'
import {
  Modal,
  Spin,
  Typography,
  Row,
  Dropdown,
  Menu,
  Button,
  Form,
  Input,
  notification,
} from 'antd'
import { LoadingOutlined } from '@ant-design/icons'
import { Bar } from '@ant-design/charts'
import { map, flatten, size } from 'lodash-es'
import {
  HTML_AUTOMATIC,
  HTML_MANUAL,
  DEBUG_SCRAPING_SETTINGS,
  MANUAL_SCRAPING_SETTINGS,
  SCRAPING_TAG_TYPES,
} from '../../../constants'
import { getAutomaticScrapingSettings } from '../../../helpers'
import { SCRAPING_TEST_CASE_COUNTS_BY_TAG_TYPES } from '../../../graphql/queries'
import { START_SCRAPING_SESSION } from '../../../graphql/mutations'
import PropTypes from 'prop-types'

const ScrapingSessionPreviewModal = ({
  scrapingSessionTemplate,
  isModalOpen,
  closeModal,
}) => {
  const [modelId, setModelId] = useState('')
  const [shouldShowModelIdError, setShouldShowModelIdError] = useState(false)
  const [isAutomaticModalOpen, setIsAutomaticModalOpen] = useState(false)
  const isModelIdInvalid = shouldShowModelIdError && !modelId.trim()

  const tagIds = useMemo(() => {
    if (!scrapingSessionTemplate) return []
    return map(flatten(map(scrapingSessionTemplate.tagGroups, 'tags')), 'id')
  }, [scrapingSessionTemplate])

  const {
    data: { scrapingTestCaseCountsByTagTypes = {} } = {},
    loading: isLoadingScrapingTestCaseCountsByType,
  } = useQuery(SCRAPING_TEST_CASE_COUNTS_BY_TAG_TYPES, {
    variables: {
      tagIds,
    },
    fetchPolicy: 'no-cache',
  })

  const resetAutomaticScrapingModal = useCallback(() => {
    setIsAutomaticModalOpen(false)
    setModelId('')
    setShouldShowModelIdError(false)
  }, [])

  const [
    startScrapingSession,
    { loading: isStartingScrapingSession },
  ] = useMutation(START_SCRAPING_SESSION, {
    onCompleted() {
      resetAutomaticScrapingModal()
      notification.open({
        message: 'Scraping session started.',
        type: 'success',
      })
      closeModal()
    },
    onError({ message }) {
      notification.open({
        message: 'Failed to start scraping session.',
        description: message,
        type: 'error',
        duration: 0,
      })
    },
  })

  const handleCloseModal = useCallback(() => {
    resetAutomaticScrapingModal()
    closeModal()
  }, [closeModal, resetAutomaticScrapingModal])

  const startScrapingSessionWithDefaultModeScraping = useCallback(() => {
    const tagGroupIds = map(scrapingSessionTemplate.tagGroups, 'id')

    return startScrapingSession({
      variables: {
        tagGroupIds,
        isDebugModeScraping: false,
        scrapingType: HTML_MANUAL,
        scrapingSettings: MANUAL_SCRAPING_SETTINGS,
      },
    })
  }, [startScrapingSession, scrapingSessionTemplate])

  const startScrapingSessionWithDebugModeScraping = useCallback(() => {
    const tagGroupIds = map(scrapingSessionTemplate.tagGroups, 'id')

    return startScrapingSession({
      variables: {
        tagGroupIds,
        isDebugModeScraping: true,
        scrapingType: HTML_MANUAL,
        scrapingSettings: DEBUG_SCRAPING_SETTINGS,
      },
    })
  }, [startScrapingSession, scrapingSessionTemplate])

  const startScrapingSessionWithAutomaticScraping = useCallback(() => {
    const trimmedModelId = modelId.trim()

    if (!trimmedModelId) {
      setShouldShowModelIdError(true)
      return null
    }

    const tagGroupIds = map(scrapingSessionTemplate.tagGroups, 'id')

    return startScrapingSession({
      variables: {
        tagGroupIds,
        isDebugModeScraping: false,
        scrapingType: HTML_AUTOMATIC,
        scrapingSettings: getAutomaticScrapingSettings(trimmedModelId),
      },
    })
  }, [modelId, scrapingSessionTemplate, startScrapingSession])

  const data = useMemo(
    () =>
      map(scrapingTestCaseCountsByTagTypes, ({ type, name, count }) => ({
        label: `${SCRAPING_TAG_TYPES[type].name}: ${name}`,
        count,
      })),
    [scrapingTestCaseCountsByTagTypes],
  )

  const barChartConfig = useMemo(
    () => ({
      data,
      height: size(data) > 10 ? size(data) * 25 : 400,
      xField: 'count',
      yField: 'label',
      seriesField: 'label',
      legend: { position: 'top-left' },
    }),
    [data],
  )

  const runButtonMenu = useMemo(
    () => (
      <Menu
        onClick={({ key }) => {
          if (key === 'debug') {
            startScrapingSessionWithDebugModeScraping()
          }

          if (key === 'automatic') {
            setIsAutomaticModalOpen(true)
          }
        }}
      >
        <Menu.Item key="debug">Run in debug mode</Menu.Item>
        <Menu.Item key="automatic">Run HTML Auto-Scraping</Menu.Item>
      </Menu>
    ),
    [startScrapingSessionWithDebugModeScraping],
  )

  return (
    <Modal
      width="1000px"
      title="Scraping session preview"
      visible={isModalOpen}
      footer={null}
      onCancel={handleCloseModal}
      destroyOnClose
    >
      {isLoadingScrapingTestCaseCountsByType ? (
        <Row justify="center">
          <Spin tip="Loading..." />
        </Row>
      ) : (
        <>
          <Typography.Text
            strong
          >{`Total test cases: ${scrapingSessionTemplate?.scrapingTestCaseCount}`}</Typography.Text>
          <Bar {...barChartConfig} />
          <Row justify="end">
            {isStartingScrapingSession ? (
              <Button
                type="primary"
                style={{ marginTop: '15px', width: '85px' }}
                icon={<LoadingOutlined />}
              />
            ) : (
              <Dropdown.Button
                type="primary"
                style={{ marginTop: '15px', width: '85px' }}
                disabled={
                  scrapingSessionTemplate?.scrapingTestCaseCount === 0 ||
                  isStartingScrapingSession
                }
                onClick={startScrapingSessionWithDefaultModeScraping}
                overlay={runButtonMenu}
              >
                Run
              </Dropdown.Button>
            )}
          </Row>
        </>
      )}
      <Modal
        title="Run HTML Auto-Scraping"
        visible={isAutomaticModalOpen}
        confirmLoading={isStartingScrapingSession}
        onOk={startScrapingSessionWithAutomaticScraping}
        onCancel={resetAutomaticScrapingModal}
        destroyOnClose
      >
        <Form.Item
          validateStatus={isModelIdInvalid ? 'error' : undefined}
          help={isModelIdInvalid ? 'Model ID is required.' : undefined}
        >
          <Input
            autoFocus
            value={modelId}
            placeholder="Model ID"
            onChange={event => setModelId(event.target.value)}
            onPressEnter={startScrapingSessionWithAutomaticScraping}
          />
        </Form.Item>
      </Modal>
    </Modal>
  )
}

ScrapingSessionPreviewModal.propTypes = {
  scrapingSessionTemplate: PropTypes.object,
  isModalOpen: PropTypes.bool.isRequired,
  closeModal: PropTypes.func.isRequired,
}

ScrapingSessionPreviewModal.defaultProps = {
  scrapingSessionTemplate: undefined,
}

export default ScrapingSessionPreviewModal
